import cx from 'classnames'
import { type ReactNode, useId } from 'react'
import {
  Table as AriaTable,
  Column as AriaTableColumn,
  TableHeader as AriaTableHeader,
  TableBody as AriaTableBody,
  Row as AriaTableRow,
  Cell as AriaTableCell,
} from 'react-aria-components'
import {
  ModalDialog,
  ModalDialogActions,
  ModalDialogContent,
  ModalDialogDescription,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogTitle,
} from '@/components/ModalDialog'
import { Button } from '@/components/Button'
import styles from './NotificationPreferencesEditorView.module.css'
import { Checkbox } from '@/components/inputs/Checkbox'
import { Skeleton } from '@/components/Skeleton'
import { useSkeletonListContext } from '@/components/Skeleton/SkeletonList'
import { Form } from '@/components/Form'

interface NotificationPreferencesEditorDialogViewProps {
  isOpen: boolean
  isPending: boolean
  formId: string
  onClose: () => void
  onSubmit: () => void
  children: ReactNode
  testId?: string
}

export function NotificationPreferencesEditorDialogView({
  isOpen,
  isPending,
  formId,
  onClose,
  onSubmit,
  children,
  testId,
}: NotificationPreferencesEditorDialogViewProps) {
  return (
    <ModalDialog
      size="lg"
      isOpen={isOpen}
      onClose={onClose}
      isDismissable={!isPending}
      testId={testId}
    >
      <ModalDialogHeader>
        <ModalDialogTitle>Notification settings</ModalDialogTitle>
        <ModalDialogDescription>
          Stay informed with all your requests.
        </ModalDialogDescription>
        <div className="pb-lg" />
      </ModalDialogHeader>
      <ModalDialogContent spacing="none" fade="bottom">
        <Form id={formId} onSubmit={onSubmit}>
          {children}
        </Form>
      </ModalDialogContent>
      <ModalDialogFooter>
        <div className="pt-lg" />
        <ModalDialogActions>
          <Button
            type="submit"
            form={formId}
            label="Apply settings"
            isPending={isPending}
          />
          <Button
            label="Cancel"
            variant="outline"
            onPress={onClose}
            isDisabled={isPending}
          />
        </ModalDialogActions>
      </ModalDialogFooter>
    </ModalDialog>
  )
}

interface PreferencesTableProps {
  children?: ReactNode
}

export function PreferencesTable({ children }: PreferencesTableProps) {
  return (
    <AriaTable aria-label="Preferences" className={styles.table}>
      <AriaTableHeader className={styles.tableHeader}>
        <AriaTableColumn className={styles.column} isRowHeader>
          Preferences
        </AriaTableColumn>
        <AriaTableColumn className={styles.checkboxColumn}>
          <div className={styles.checkboxColumnText}>Email</div>
        </AriaTableColumn>
        <AriaTableColumn className={styles.checkboxColumn}>
          <div className={styles.checkboxColumnText}>Push</div>
        </AriaTableColumn>
      </AriaTableHeader>
      <AriaTableBody>{children}</AriaTableBody>
    </AriaTable>
  )
}

interface PreferenceRowProps {
  title: string
  description?: string
  emailEnabled: boolean
  pushEnabled: boolean
  isEmailDisabled?: boolean
  isPushDisabled?: boolean
  isDisabled?: boolean
  onEmailChange: (value: boolean) => void
  onPushChange: (value: boolean) => void
}

export function PreferenceRow({
  title,
  description,
  emailEnabled,
  pushEnabled,
  isEmailDisabled,
  isPushDisabled,
  isDisabled,
  onEmailChange,
  onPushChange,
}: PreferenceRowProps) {
  const emailCheckboxId = useId()
  const pushCheckboxId = useId()
  const textValue = [title, description].filter(Boolean).join('. ')

  return (
    <AriaTableRow
      className={cx(styles.row)}
      textValue={textValue}
      isDisabled={isDisabled}
    >
      <AriaTableCell className={styles.cell}>
        <p className={styles.rowTitle}>{title}</p>
        {!!description && (
          <p className={styles.rowDescription}>{description}</p>
        )}
      </AriaTableCell>
      <AriaTableCell className={styles.checkboxCell}>
        <label
          className={styles.checkboxContainer}
          htmlFor={emailCheckboxId}
          aria-label={`Email enabled for ${title}`}
        >
          <Checkbox
            id={emailCheckboxId}
            checked={emailEnabled}
            onChange={onEmailChange}
            disabled={isEmailDisabled || isDisabled}
          />
        </label>
      </AriaTableCell>
      <AriaTableCell className={styles.checkboxCell}>
        <label
          className={styles.checkboxContainer}
          htmlFor={pushCheckboxId}
          aria-label={`Push enabled for ${title}`}
        >
          <Checkbox
            id={pushCheckboxId}
            checked={pushEnabled}
            onChange={onPushChange}
            disabled={isPushDisabled || isDisabled}
          />
        </label>
      </AriaTableCell>
    </AriaTableRow>
  )
}

export function PreferenceRowSkeleton() {
  const { varyPercent, index } = useSkeletonListContext()
  return (
    <AriaTableRow className={cx(styles.row)} textValue="Placeholder" isDisabled>
      <AriaTableCell className={styles.cell}>
        <p className={styles.rowTitle}>
          <Skeleton variant="text" width={varyPercent(40)} />
        </p>

        {index !== 0 && (
          <p className={styles.rowDescription}>
            <Skeleton variant="text" width={varyPercent(70)} />
            {index % 3 === 0 && (
              <Skeleton variant="text" width={varyPercent(30)} />
            )}
          </p>
        )}
      </AriaTableCell>
      <AriaTableCell className={styles.checkboxCell}>
        <div className={styles.checkboxContainer}>
          <Checkbox aria-label="Placeholder" isSkeleton />
        </div>
      </AriaTableCell>
      <AriaTableCell className={styles.checkboxCell}>
        <div className={styles.checkboxContainer}>
          <Checkbox aria-label="Placeholder" isSkeleton />
        </div>
      </AriaTableCell>
    </AriaTableRow>
  )
}
